import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { DEFAULT_PAGE_SIZE } from '@awp/constants';
import { getNumberFromSearchParams } from '@awp/helpers/navigation';

const UrlParamTableFilterContext = createContext();

export const UrlParamTableFilterContextProvider = ({ children }) => {
  const getPageSize = () => {
    return getNumberFromSearchParams('pageSize', DEFAULT_PAGE_SIZE);
  };

  const getIndex = () => {
    return getNumberFromSearchParams('index', 0);
  };

  const getSort = () => {
    const param = searchParams.get('sort');
    try {
      return JSON.parse(param);
    } catch (error) {
      this.searchParams.delete('sort');
      return null;
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [defaultPageSize, setDefaultPageSize] = useState(getPageSize());
  const [sort, setSort] = useState(getSort());
  const [index, setIndex] = useState(null);
  const [initialIndex, setInitialIndex] = useState(getIndex());

  const search = searchParams.get('search') ?? '';
  const loading = children.props.loading;

  useEffect(() => {
    // When searchParams, store it to Session Storage
    sessionStorage.setItem('listSearchParams', JSON.stringify(searchParams.toString()));
  }, [searchParams]);

  const onChangeIndex = (myIndex) => {
    if (index !== null && loading === false) {
      let indexResult = myIndex;

      setSearchParams((params) => {
        const name = 'index';

        // React-Table tries to change the index back to zero after initializing.
        // If the initialIndex hasn't been used, ignore index changes
        if (isValueEmpty(myIndex) || (myIndex === 0 && initialIndex === null)) {
          params.delete(name);
        } else {
          if (initialIndex !== null) {
            indexResult = initialIndex;
            setInitialIndex(null);
          }

          params.set(name, indexResult);
        }

        return params;
      });

      setIndex(indexResult);
    }
  };

  useEffect(() => {
    if (initialIndex !== index && index === null && children.props.loading === false) {
      // Index Initialization
      if (initialIndex === 0) {
        setInitialIndex(null);
        setIndex(0);
      } else {
        setIndex(initialIndex);
      }
    }
  }, [index, loading]);

  const onChangePageSize = (myPageSize) => {
    setSearchParams((params) => {
      params.set('pageSize', myPageSize);

      return params;
    });

    setDefaultPageSize(myPageSize);
  };

  const onChangeSearch = (e) => {
    const { name, value } = e ? e.target : undefined;

    setSearchParams((params) => {
      if (isValueEmpty(value)) {
        params.delete(name);
      } else {
        params.set(name, value);
      }

      return params;
    });

    setIndex(0);
  };

  const onChangeSort = (mySort) => {
    setSearchParams((params) => {
      params.set('sort', JSON.stringify(mySort));

      return params;
    });
  };

  const stateValues = {
    defaultPageSize,
    index,
    search,
    sort,
  };

  const stateSetters = {
    setDefaultPageSize,
    setIndex,
    setSort,
  };

  const handlers = {
    onChangeIndex,
    onChangePageSize,
    onChangeSearch,
    onChangeSort,
  };

  const contextValues = {
    ...stateValues,
    ...stateSetters,
    ...handlers,
  };

  return <UrlParamTableFilterContext.Provider value={contextValues}>{children}</UrlParamTableFilterContext.Provider>;
};

export const useUrlParamTableFilterContext = () => useContext(UrlParamTableFilterContext);
